import { useCallback, useLayoutEffect, useRef, useState, type Ref } from 'react';
import classNames from 'classnames';
import FJDropDown from '@src/common/components/FJDropDown/FJDropDown';
import FJScroller from '@src/common/components/FJScroller/FJScroller';
import FJExpandIcon from '../../assets/expand.svg?react';
import FJCheckIcon from '../../assets/ok.svg?react';
import styles from './FJGeneratingSettingsPanel.module.less';
import FJToolTip from '@src/common/components/FJToolTip/FJToolTip';

interface FJGeneratingSettingsSelectOption {
    value: string;
    label: string;
    disabled?: boolean;
}

interface FJGeneratingSettingsSelectProps {
    value: string;
    options: FJGeneratingSettingsSelectOption[];
    onChange: (value: string) => void;
    /** 供无障碍与 `aria-label` */
    ariaLabel: string;
}

export default function FJGeneratingSettingsSelect({
    value,
    options,
    onChange,
    ariaLabel,
}: FJGeneratingSettingsSelectProps) {
    const measureRef = useRef<HTMLDivElement>(null);
    const selectedOptionRef = useRef<HTMLButtonElement>(null);
    const [menuWidth, setMenuWidth] = useState(0);

    const scrollSelectedIntoView = useCallback(() => {
        // `block: 'center'` 会滚动整页/祖先，配合 FJDropDown 的 isNeedBindWheel 时
        // 在 document 上监听的 `scroll` 会立刻关菜单；`nearest` 仅尽量在层内滚，避免误关。
        selectedOptionRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, []);

    const onDropDownShow = useCallback(() => {
        requestAnimationFrame(() => {
            requestAnimationFrame(scrollSelectedIntoView);
        });
        // 与 FJDropDown 内 `visibility` 切换时机对齐，避免首帧尚未可滚动
        window.setTimeout(scrollSelectedIntoView, 220);
    }, [scrollSelectedIntoView]);

    useLayoutEffect(() => {
        const el = measureRef.current;
        if (!el) {
            return;
        }
        const update = () => setMenuWidth(el.offsetWidth);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const selected = options.find(o => o.value === value);
    const displayLabel = selected?.label ?? value;

    return (
        <div ref={measureRef} className={styles.dropdown_measure}>
            <FJDropDown
                placement='bottomLeft'
                zIndex={40}
                isTopLayer
                canTriggerActive
                isNeedBindWheelEvent={false}
                onShow={onDropDownShow}
                overlayStyle={menuWidth > 0 ? { width: menuWidth } : undefined}
                offsetTop={6}
                overlayClassName={styles.dropdown_overlay}
                triggerDisplay={'flex'}
                renderTrigger={({ ref, style, onClick, active }) => (
                    <button
                        type='button'
                        ref={ref as Ref<HTMLButtonElement>}
                        className={styles.dropdown_trigger}
                        style={{ ...style, width: '100%' }}
                        onClick={onClick}
                        aria-label={ariaLabel}
                        aria-haspopup='listbox'
                        aria-expanded={Boolean(active)}
                    >
                        <span className={styles.dropdown_trigger_text}>{displayLabel}</span>
                        <FJExpandIcon className={styles.dropdown_trigger_icon} aria-hidden />
                    </button>
                )}
                renderOverlay={({ hide, ref }) => (
                    <FJScroller
                        ref={ref as Ref<HTMLDivElement>}
                        axis='y'
                        className={styles.dropdown_list_scroll}
                        role='listbox'
                        aria-label={ariaLabel}
                    >
                        <ul className={styles.dropdown_list}>
                            {options.map(opt => (
                                <li key={opt.value} role='presentation'>
                                    <FJToolTip title={opt?.tip ?? ''}>
                                        <button
                                            type='button'
                                            ref={opt.value === value ? selectedOptionRef : undefined}
                                            role='option'
                                            aria-selected={opt.value === value}
                                            disabled={opt.disabled}
                                            aria-disabled={opt.disabled ? true : undefined}
                                            className={classNames(
                                                styles.dropdown_option,
                                                opt.value === value && styles.dropdown_option_selected,
                                                opt.disabled && styles.dropdown_option_disabled,
                                            )}
                                            onClick={() => {
                                                if (opt.disabled) return;
                                                onChange(opt.value);
                                                hide();
                                            }}
                                        >
                                            {opt.label}
                                            {opt.value === value && (
                                                <FJCheckIcon className={styles.dropdown_option_icon} aria-hidden />
                                            )}
                                        </button>
                                    </FJToolTip>
                                </li>
                            ))}
                        </ul>
                    </FJScroller>
                )}
            />
        </div>
    );
}
